import React, { useState, useEffect, useRef } from 'react';
import DataTable from './DataTable';
import { getUsers, updateUserRole, deleteUser, resetUserTimer, getUserMappings, deleteMapping } from '../../lib/adminApi';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [editRole, setEditRole] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetTimerConfirm, setResetTimerConfirm] = useState(null);
  const [viewMappings, setViewMappings] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);

  // Ref to prevent double API calls in StrictMode
  const hasFetched = useRef(false);

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const data = await getUsers(page, pagination.limit);
      setUsers(data.users || []);
      setPagination({ page: data.page, limit: data.limit, total: data.total });
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchUsers();
  }, []);

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      setEditRole(null);
      fetchUsers(pagination.page);
    } catch (err) {
      console.error('Failed to update role:', err);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await deleteUser(userId);
      setDeleteConfirm(null);
      fetchUsers(pagination.page);
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const handleResetTimer = async (userId) => {
    try {
      await resetUserTimer(userId);
      setResetTimerConfirm(null);
      fetchUsers(pagination.page);
    } catch (err) {
      console.error('Failed to reset timer:', err);
    }
  };

  const handleViewMappings = async (user) => {
    setViewMappings(user);
    setMappingsLoading(true);
    try {
      const data = await getUserMappings(user.userId);
      setMappings(data.mappings || []);
    } catch (err) {
      console.error('Failed to fetch mappings:', err);
      setMappings([]);
    } finally {
      setMappingsLoading(false);
    }
  };

  const handleDeleteMapping = async (userId, questionId) => {
    try {
      await deleteMapping(userId, questionId);
      // Refresh mappings after deletion
      const data = await getUserMappings(userId);
      setMappings(data.mappings || []);
    } catch (err) {
      console.error('Failed to delete mapping:', err);
    }
  };

  const columns = [
    { key: 'userId', label: 'ID' },
    { key: 'email', label: 'Email' },
    {
      key: 'role',
      label: 'Role',
      render: (val) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            val === 'admin'
              ? 'bg-purple-600/20 text-purple-400'
              : 'bg-gray-600/20 text-gray-400'
          }`}
        >
          {val || 'user'}
        </span>
      ),
    },
  ];

  const actions = (row) => (
    <div className="flex gap-2 justify-end">
      <button
        onClick={() => handleViewMappings(row)}
        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
      >
        View Mappings
      </button>
      <button
        onClick={() => setResetTimerConfirm(row)}
        className="px-3 py-1 bg-cyan-600 text-white rounded text-sm hover:bg-cyan-700"
      >
        Reset Timer
      </button>
      <button
        onClick={() => setEditRole(row)}
        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
      >
        Edit Role
      </button>
      <button
        onClick={() => setDeleteConfirm(row)}
        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
      >
        Delete
      </button>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Users</h1>
      </div>

      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        pagination={pagination}
        onPageChange={fetchUsers}
        actions={actions}
        emptyMessage="No users found."
      />

      {/* Edit Role Modal */}
      {editRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Edit User Role</h2>
            <p className="text-gray-300 mb-4">
              User: <strong>{editRole.email}</strong>
            </p>
            <div className="space-y-2 mb-6">
              <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded cursor-pointer hover:bg-gray-700">
                <input
                  type="radio"
                  name="role"
                  value="user"
                  checked={editRole.role !== 'admin'}
                  onChange={() => setEditRole({ ...editRole, role: 'user' })}
                  className="w-4 h-4"
                />
                <div>
                  <div className="text-white font-medium">User</div>
                  <div className="text-gray-400 text-sm">Regular user access</div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded cursor-pointer hover:bg-gray-700">
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={editRole.role === 'admin'}
                  onChange={() => setEditRole({ ...editRole, role: 'admin' })}
                  className="w-4 h-4"
                />
                <div>
                  <div className="text-white font-medium">Admin</div>
                  <div className="text-gray-400 text-sm">Full admin access</div>
                </div>
              </label>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setEditRole(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateRole(editRole.userId, editRole.role)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Confirm Delete</h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete user <strong>{deleteConfirm.email}</strong>?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.userId)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Timer Confirmation Modal */}
      {resetTimerConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Reset Timer</h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to reset the timer for user <strong>{resetTimerConfirm.email}</strong>?
              <br />
              <span className="text-yellow-400 text-sm">
                This will restart their countdown from now.
              </span>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setResetTimerConfirm(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleResetTimer(resetTimerConfirm.userId)}
                className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700"
              >
                Reset Timer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Mappings Modal */}
      {viewMappings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-3xl w-full mx-4 border border-gray-700 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">
                Question Mappings for {viewMappings.email}
              </h2>
              <button
                onClick={() => setViewMappings(null)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                &times;
              </button>
            </div>

            {mappingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
              </div>
            ) : mappings.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No question mappings found for this user.</p>
            ) : (
              <div className="overflow-y-auto flex-1">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-800">
                    <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                      <th className="py-3 px-4">Question ID</th>
                      <th className="py-3 px-4">Attempts</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map((mapping) => (
                      <tr key={mapping.questionId} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-3 px-4 text-white">Q{mapping.questionId}</td>
                        <td className="py-3 px-4 text-gray-300">{mapping.noOfAttempts || 0}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            mapping.solved
                              ? 'bg-green-600/20 text-green-400'
                              : 'bg-yellow-600/20 text-yellow-400'
                          }`}>
                            {mapping.solved ? 'Solved' : 'In Progress'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => handleDeleteMapping(viewMappings.userId, mapping.questionId)}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-700 flex justify-between items-center">
              <p className="text-gray-400 text-sm">
                Total: {mappings.length} mapping(s)
              </p>
              <button
                onClick={() => setViewMappings(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
